import type { NavApi } from "../../shell/router";
import type { UIEvent } from "../../lib/events-adapter";
import { toast } from "../../shell/toast";
import { CreationStore } from "./creation-store";
import { creation, exportEventGuests } from "@v0y4ge/api-client";
import * as ApiClient from "@v0y4ge/api-client";
import { refetchEvents } from "./useEvents";
import { T } from "@v0y4ge/jrni-ui";
import { upsertTemplate, newTemplateId } from "../templates/useTemplates";
import { publicUrlForTitle, slugify, ensurePublicConfig } from "../../lib/public-url";
import { t as i18nT } from "../../lib/i18n";

/**
 * Shared event-action helpers — verbatim port of the prototype's
 * window.eventActions. Centralises the "do X to event Y" intents used from the
 * table row actions, the detail drawer header, and both MoreMenus, so the same
 * action behaves identically wherever it's triggered.
 *
 * Mutations are wired to the typed @v0y4ge/api-client ops and AWAITED — success
 * toasts fire only after the call resolves; failures toast a real error
 * (no-fake-interactivity mandate, #713/#714).
 */
function publicUrl(ev: UIEvent): string {
  // Synchronous pre-round-trip fallback only — the canonical URL comes from
  // getPublicEventPage().publicUrl (data layer owns host resolution).
  const slug = (ev.raw as unknown as { slug?: string })?.slug;
  return slug ? publicUrlForTitle(slug) : publicUrlForTitle(ev.title || ev.id);
}
function eventSlug(ev: UIEvent): string {
  return (ev.raw as unknown as { slug?: string })?.slug || slugify(ev.title || ev.id);
}

/** Resolve the canonical attend.* public URL via getPublicEventPage — the data
 *  layer owns host resolution. Returns null when the op is unavailable/fails. */
async function fetchPublicUrl(slug: string): Promise<string | null> {
  const op = (ApiClient as unknown as Record<string, unknown>).getPublicEventPage;
  if (typeof op !== "function") return null;
  try {
    const r = await (op as (slug: string) => Promise<unknown>)(slug) as Record<string, any>;
    return r?.publicUrl ?? r?.page?.publicUrl ?? r?.data?.publicUrl ?? null;
  } catch (e) { console.warn("getPublicEventPage failed — composing fallback URL:", e); return null; }
}

/** Open the real public event page in a new tab (smoke bug B, 2026-07-09):
 *  resolve via getPublicEventPage().publicUrl the same way copyPublicUrl does,
 *  then open externally — NEVER nav.go("event.public") (a Studio-internal
 *  route that 404s; the public site lives on the attend.* host). The tab opens
 *  synchronously inside the click gesture (popup-blocker-safe), then navigates
 *  to the resolved URL; publicUrlForTitle is the pre-round-trip fallback
 *  composer. Accepts a slug or a display title. */
export async function openPublicEventPage(slugOrTitle: string): Promise<void> {
  const slug = slugify(slugOrTitle || "");
  const win = window.open("", "_blank");
  if (win) { try { (win as Window & { opener: unknown }).opener = null; } catch { /* noop */ } }
  let url = await fetchPublicUrl(slug);
  if (!url) {
    // Warm the tenant public-config cache before composing the fallback —
    // otherwise a standalone route races the loader and composes
    // `attend.your-workspace/<slug>` (2026-07-09 amendment / ensurePublicConfig).
    await ensurePublicConfig();
    url = publicUrlForTitle(slug);
  }
  if (win) win.location.href = url;
  else window.open(url, "_blank", "noopener");
}

export const eventActions = { // ui-config — action handlers (functions), not data
  editEvent: (ev: UIEvent, nav: NavApi) => { // ui-local — loads wizard state + navigates; no persistence
    // #729: pass the raw contract row too (as `.raw`) so loadEvent can find the
    // creationSnapshot the wizard itself saved — previously only the flat
    // UIEvent (no snapshot) was passed, so edit always fell back to a
    // title/date-only reload and silently dropped description + hero image.
    /* The wizard's prefill-then-open is gone with the wizard (2026-08-24). The
       designer reads the event record itself — a journey keyed `event:<id>` —
       so Edit hands it the id and nothing needs pre-loading. */
    nav.closeEvent();
    nav.go("newjourneys", { journey: `event:${ev.id}` });
  },
  openLandingPage: (ev: UIEvent, nav: NavApi) => { nav.closeEvent(); void openPublicEventPage(eventSlug(ev)); }, // smoke bug B — opens the real attend.* page, not a Studio route
  copyPublicUrl: async (ev: UIEvent) => {
    // Canonical URL from the contracted op (0707 handback: data layer owns host
    // resolution); the composer is only the fallback when the op hasn't
    // round-tripped / isn't available.
    const url = await fetchPublicUrl(eventSlug(ev));
    try { void navigator.clipboard?.writeText(url || publicUrl(ev)); } catch { /* noop */ }
    toast({ title: i18nT("events.actions.publicUrlCopied", "Public URL copied"), tone: "success" });
  },
  showCheckInQR: (ev: UIEvent) => toast({ tone: "info", title: "Check-in QR — coming soon", subtitle: `A scannable check-in QR for ${ev.title} is coming soon.` }), // coming-soon affordance — no QR renderer wired yet
  saveAsTemplate: async (ev: UIEvent, nav: NavApi) => {
    // Create a real, reusable Event Template from this event so it lands in the
    // Templates library — shareable with the team and usable to spin up new
    // events ("Use this template"). The template's presets derive from the
    // event's own configuration; the awaited createEventTemplate op persists it
    // (`sourceEventId` lets the backend deep-copy the event's tickets/comms/
    // questions server-side rather than trusting the UI summary).
    const raw = (ev.raw || {}) as unknown as { ticketTypeIds?: unknown[]; ticketGroupIds?: unknown[] };
    const ticketCount = (Array.isArray(raw.ticketTypeIds) ? raw.ticketTypeIds.length : 0) + (Array.isArray(raw.ticketGroupIds) ? raw.ticketGroupIds.length : 0);
    // Full-definition snapshot (operator ask 2026-07-16): carry EVERYTHING from
    // the source event EXCEPT dates/times and registration open/close (those are
    // occurrence-specific). Applied at use/edit time via CreationStore.applyTemplate.
    const TEMPLATE_EXCLUDE = new Set([
      "id", "startsAt", "endsAt", "starts_at", "ends_at", "start_utc", "end_utc",
      "registrationOpensAt", "registrationClosesAt", "registration_opens_at", "registration_closes_at",
      "status", "registeredCount", "registration_count", "waitlist_count", "waitlistCount",
      "createdAt", "updatedAt", "created_at", "updated_at", "slug", "publicUrl", "public_url",
    ]);
    const fullRaw = (ev.raw || {}) as unknown as Record<string, unknown>;
    const config: Record<string, unknown> = { sourceEventId: ev.id };
    Object.entries(fullRaw).forEach(([k, v]) => { if (!TEMPLATE_EXCLUDE.has(k) && v !== undefined && v !== null) config[k] = v; });
    if (config.registrationConfig && typeof config.registrationConfig === "object") {
      // Strip nested registration-window datetimes; keep the rest of the config.
      config.registrationConfig = Object.fromEntries(Object.entries(config.registrationConfig as Record<string, unknown>).filter(([k]) => !/opens|closes/i.test(k)));
    }
    const name = `${ev.title} Template`;
    const derived = {
      typeId: "blank",
      name,
      type: ev.template || "Event",
      usage: 0,
      lastEdit: "Just now",
      hue: ev.hue || T.p1,
      desc: ev.description || `Reusable blueprint saved from \u201c${ev.title}\u201d \u2014 tickets, comms and check-in preset. Share it with your team to spin up similar events.`,
      tags: Array.isArray(ev.tags) ? ev.tags : [],
      defaults: { capacity: ev.capacity || 0, tickets: ticketCount, comms: 0 },
      status: "published" as const,
    };
    try {
      // Typed payload (drop-34 docket): EventTemplateCreateInput is STRICT —
      // `usage`/`lastEdit` are client-display fields (local store row only) and
      // `config.sourceEventId` (server-side deep-copy of tickets/comms/questions)
      // is NOT in the contract yet — declared in the 2026-07-11T1500 changelog
      // for reconciliation. Until it lands, the template carries the derived
      // summary only.
      const r = await creation.createEventTemplate({
        typeId: derived.typeId, name: derived.name, type: derived.type,
        hue: derived.hue, desc: derived.desc, tags: derived.tags,
        defaults: derived.defaults, status: derived.status,
      });
      const createdId = (r as { template?: { id?: string } } | undefined)?.template?.id;
      // Reflect it in the shared template store immediately so it appears in the
      // library without a refetch (kept in sync across a listEventTemplates merge).
      upsertTemplate({ id: createdId || newTemplateId(), ...derived, config });
      toast({ tone: "success", title: i18nT("events.actions.savedAsTemplate", "Saved as template"), subtitle: i18nT("events.actions.u201cNameU201dIsNowInEventTemplatesU2014ReadyToShare.icu", { value0: name }) });
      nav.closeEvent();
      nav.go("templates");
    } catch (e) {
      console.warn("createEventTemplate failed:", e);
      toast({ tone: "danger", title: i18nT("events.actions.couldnTSaveTemplate", "Couldn't save template"), subtitle: i18nT("events.actions.nothingWasSavedRetryInAMoment", "Nothing was saved. Retry in a moment.") });
    }
  },
  duplicateEvent: async (ev: UIEvent) => {
    try {
      // Typed payload (drop-34 docket): the previous `creationSnapshot:
      // { sourceEventId, duplicateOf }` was NOT a conforming CreationSnapshot
      // (which requires the full draft shape) — the cast was hiding that. A
      // duplicate-by-reference needs a first-class input field (declared:
      // `duplicateOfEventId` on CreationEventInput, 2026-07-11T1500 changelog);
      // until it lands the copy is a titled draft only.
      await creation.createEvent({ title: i18nT("events.actions.evTitleCopy.icu", { value0: ev.title }), status: "draft" });
      void refetchEvents(); // docket §1c — server rows win; the new draft appears without a remount
      toast({ tone: "success", title: i18nT("events.actions.eventDuplicated", "Event duplicated"), subtitle: i18nT("events.actions.aDraftCopyOfEvTitleWasAdded.icu", { value0: ev.title }) });
    } catch (e) {
      console.warn("duplicateEvent (createEvent) failed:", e);
      toast({ tone: "danger", title: i18nT("events.actions.couldnTDuplicateEvent", "Couldn't duplicate event"), subtitle: i18nT("events.actions.noCopyWasCreatedRetryInAMoment", "No copy was created. Retry in a moment.") });
    }
  },
  exportGuestList: async (ev: UIEvent) => {
    try {
      // exportEventGuests RETURNS the roster as a text/csv string (the events
      // worker streams it synchronously — 2026-07-14 docket). Capture it and
      // download client-side; the previous code discarded the CSV and showed a
      // false "we'll email you the link" toast (no email delivery exists).
      const csv = await (exportEventGuests as (id: string) => Promise<string>)(ev.id);
      const filename = `${slugify(ev.title || ev.id)}-guests.csv`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.rel = "noopener"; a.style.display = "none";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast({ tone: "success", title: i18nT("events.actions.guestListExported", "Guest list exported"), subtitle: i18nT("events.actions.csvDownloadedForEvTitle.icu", { value0: ev.title }) });
    } catch (e) {
      console.warn("exportEventGuests failed:", e);
      toast({ tone: "danger", title: i18nT("events.actions.exportDidnTStart", "Export didn't start"), subtitle: i18nT("events.actions.retryInAMoment", "Retry in a moment.") });
    }
  },
  cancelOrDiscard: async (ev: UIEvent, nav: NavApi) => {
    const isDraft = ev.status === "draft";
    try {
      if (isDraft) await creation.discardEvent(ev.id);
      else await creation.cancelEvent(ev.id);
      void refetchEvents(); // docket §1c — reflect the cancel/discard on the already-open list
      toast({ tone: "warning", title: isDraft ? "Draft discarded" : "Event cancelled", subtitle: isDraft ? `"${ev.title}" was removed.` : `Registered guests for "${ev.title}" will be notified.` });
      nav.closeEvent();
    } catch (e) {
      console.warn(isDraft ? "discardEvent failed:" : "cancelEvent failed:", e);
      toast({ tone: "danger", title: isDraft ? "Couldn't discard draft" : "Couldn't cancel event", subtitle: i18nT("events.actions.theEventIsUnchangedRetryInAMoment", "The event is unchanged. Retry in a moment.") });
    }
  },
};
